import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  PenLine,
  BookText,
  Loader2,
  Sparkles,
  Search,
  BookOpen,
  Palette,
  Ruler,
  Settings2,
} from "lucide-react";
import { toast } from "sonner";
import { useLocale } from "@/hooks/useLocale";

// ── Types ──

export interface StoryWriterSettings {
  guidelines: string;
  use_rag: boolean;
  include_journal: boolean;
  writing_length: "short" | "medium" | "long";
  style_preset: string;
  custom_style: string;
  pov: "first" | "third" | "third_limited" | "omniscient";
  tone: string;
}

const STYLE_PRESETS = [
  { id: "literary", label: "Literary Fiction", desc: "Elegant prose, rich metaphors, character-driven" },
  { id: "pulp", label: "Pulp Adventure", desc: "Fast-paced, action-forward, vivid descriptions" },
  { id: "dark_fantasy", label: "Dark Fantasy", desc: "Gothic atmosphere, brooding, visceral" },
  { id: "epic", label: "Epic Saga", desc: "Grand scale, mythic language, sweeping narrative" },
  { id: "noir", label: "Noir", desc: "Cynical voice, atmospheric, morally gray" },
  { id: "cozy", label: "Cozy Adventure", desc: "Warm, gentle, focus on relationships and small moments" },
  { id: "horror", label: "Horror", desc: "Tension, dread, psychological unease" },
  { id: "comedic", label: "Comedic", desc: "Witty, irreverent, absurdist humor" },
] as const;

const TONE_OPTIONS = [
  "Serious", "Whimsical", "Melancholic", "Hopeful",
  "Gritty", "Romantic", "Mysterious", "Satirical",
];

const LENGTH_CONFIG = {
  short: { label: "Short", desc: "~50-100 words per beat", words: "50-100" },
  medium: { label: "Medium", desc: "~100-200 words per beat", words: "100-200" },
  long: { label: "Long", desc: "~200-400 words per beat", words: "200-400" },
};

const DEFAULT_SETTINGS: StoryWriterSettings = {
  guidelines: "",
  use_rag: false,
  include_journal: false,
  writing_length: "short",
  style_preset: "",
  custom_style: "",
  pov: "third_limited",
  tone: "",
};

// ── localStorage persistence ──

function settingsKey(adventureId: string, type: "diary" | "story") {
  return `story-writer-settings-${adventureId}-${type}`;
}

export function loadWriterSettings(adventureId: string, type: "diary" | "story"): StoryWriterSettings {
  try {
    const raw = localStorage.getItem(settingsKey(adventureId, type));
    if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {}
  return { ...DEFAULT_SETTINGS };
}

export function saveWriterSettings(adventureId: string, type: "diary" | "story", settings: StoryWriterSettings) {
  try {
    localStorage.setItem(settingsKey(adventureId, type), JSON.stringify(settings));
  } catch {}
}

// ── Load world-level Story Writer agent defaults ──

export async function loadWorldWriterDefaults(worldId: string): Promise<Partial<StoryWriterSettings> | null> {
  try {
    const { data } = await supabase
      .from("agent_definitions")
      .select("config, personality, system_prompt_template")
      .eq("world_id", worldId)
      .eq("role", "story_writer")
      .is("deleted_at", null)
      .maybeSingle();
    if (!data) return null;
    const config = (data.config as any) || {};
    return {
      style_preset: config.style_preset || "",
      custom_style: config.custom_style || "",
      pov: config.pov || "third_limited",
      tone: config.tone || "",
      writing_length: config.writing_length || "medium",
      use_rag: config.use_rag ?? false,
      include_journal: config.include_journal ?? false,
    };
  } catch {
    return null;
  }
}

// ── Dialog Component ──

export function StoryWriterDialog({
  open,
  type,
  settings,
  onSettingsChange,
  onConfirm,
  onCancel,
  isGenerating,
  modeLabel,
  hasDiaryEntries,
}: {
  open: boolean;
  type: "diary" | "story";
  settings: StoryWriterSettings;
  onSettingsChange: (s: StoryWriterSettings) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isGenerating: boolean;
  modeLabel?: string;
  hasDiaryEntries: boolean;
}) {
  const { t } = useLocale();
  const isDiary = type === "diary";
  const update = (patch: Partial<StoryWriterSettings>) =>
    onSettingsChange({ ...settings, ...patch });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            {isDiary ? <PenLine className="w-4 h-4 text-primary" /> : <BookText className="w-4 h-4 text-primary" />}
            {modeLabel || (isDiary ? "Journal Entry Settings" : "Story Writer Settings")}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Configure how the AI writes your {isDiary ? "journal entry" : "story chapter"}.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 -mx-6 px-6">
          <div className="space-y-4 pb-2">
            {/* Guidelines prompt */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <Settings2 className="w-3 h-3" /> Guidance Prompt
              </Label>
              <Textarea
                placeholder={isDiary
                  ? "e.g. Write with a melancholic tone. Focus on relationships."
                  : "e.g. Focus on the battle scene. Make the villain more menacing."}
                value={settings.guidelines}
                onChange={(e) => update({ guidelines: e.target.value })}
                className="min-h-[60px] text-xs resize-none"
              />
            </div>

            <Separator />

            {/* Feature toggles */}
            <div className="space-y-3">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <Search className="w-3 h-3" /> Data Sources
              </Label>

              <div className="space-y-2">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox
                    checked={settings.use_rag}
                    onCheckedChange={(v) => update({ use_rag: !!v })}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-xs font-medium">Use World Knowledge (RAG)</span>
                    <p className="text-[10px] text-muted-foreground">
                      Search world lore, entities, and documents the player has discovered to enrich prose with canonical details.
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox
                    checked={settings.include_journal}
                    onCheckedChange={(v) => update({ include_journal: !!v })}
                    disabled={!hasDiaryEntries && !isDiary}
                    className="mt-0.5"
                  />
                  <div>
                    <span className={cn("text-xs font-medium", !hasDiaryEntries && !isDiary && "text-muted-foreground")}>
                      {isDiary ? "Include Previous Journal Entries" : "Use Journal Entries as Base"}
                    </span>
                    <p className="text-[10px] text-muted-foreground">
                      {isDiary
                        ? "Reference previous diary entries for voice continuity and emotional arcs."
                        : hasDiaryEntries
                          ? "Feed diary entries into the story writer for emotional depth and internal perspective."
                          : "No journal entries yet — write some diary entries first."}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            <Separator />

            {/* Writing Length */}
            <div className="space-y-2">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <Ruler className="w-3 h-3" /> Writing Length (per beat)
              </Label>
              <p className="text-[10px] text-muted-foreground">
                The AI identifies narrative beats (combat, discovery, dialogue, travel) and writes each one at this length.
              </p>
              <div className="grid grid-cols-3 gap-1.5">
                {(["short", "medium", "long"] as const).map((len) => (
                  <button
                    key={len}
                    onClick={() => update({ writing_length: len })}
                    className={cn(
                      "p-2 rounded-lg border text-center transition-all",
                      settings.writing_length === len
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border bg-card text-muted-foreground hover:border-primary/40"
                    )}
                  >
                    <div className="text-xs font-medium">{LENGTH_CONFIG[len].label}</div>
                    <div className="text-[10px] text-muted-foreground">{LENGTH_CONFIG[len].words}w</div>
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            {/* POV */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Point of View</Label>
              <div className="grid grid-cols-2 gap-1.5">
                {([
                  { id: "first", label: "1st Person", desc: "I walked into…" },
                  { id: "third_limited", label: "3rd Limited", desc: "She felt the cold…" },
                  { id: "third", label: "3rd Person", desc: "The hero advanced…" },
                  { id: "omniscient", label: "Omniscient", desc: "All parties converged…" },
                ] as const).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => update({ pov: p.id })}
                    className={cn(
                      "p-2 rounded-lg border text-left transition-all",
                      settings.pov === p.id
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border bg-card text-muted-foreground hover:border-primary/40"
                    )}
                  >
                    <div className="text-xs font-medium">{p.label}</div>
                    <div className="text-[10px] text-muted-foreground italic">{p.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            {/* Style Presets */}
            <div className="space-y-2">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <Palette className="w-3 h-3" /> Style Preset
              </Label>
              <div className="grid grid-cols-2 gap-1.5">
                {STYLE_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => update({ style_preset: settings.style_preset === preset.id ? "" : preset.id })}
                    className={cn(
                      "p-2 rounded-lg border text-left transition-all",
                      settings.style_preset === preset.id
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border bg-card text-muted-foreground hover:border-primary/40"
                    )}
                  >
                    <div className="text-xs font-medium">{preset.label}</div>
                    <div className="text-[10px] text-muted-foreground">{preset.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Tone */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Tone</Label>
              <div className="flex flex-wrap gap-1.5">
                {TONE_OPTIONS.map((t) => (
                  <Badge
                    key={t}
                    variant={settings.tone === t.toLowerCase() ? "default" : "outline"}
                    className="cursor-pointer text-[10px]"
                    onClick={() => update({ tone: settings.tone === t.toLowerCase() ? "" : t.toLowerCase() })}
                  >
                    {t}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Custom Style */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Custom Style Notes</Label>
              <Textarea
                placeholder={t.play_story_style_placeholder}
                value={settings.custom_style}
                onChange={(e) => update({ custom_style: e.target.value })}
                className="min-h-[50px] text-xs resize-none"
              />
              <p className="text-[10px] text-muted-foreground">
                Overrides or supplements the style preset. Saved per adventure.
              </p>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-border">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isGenerating}>
            Cancel
          </Button>
          <Button size="sm" onClick={onConfirm} disabled={isGenerating} className="gap-1.5">
            {isGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
